/**
 * Storage service for managing data persistence in localStorage
 */
import * as LocalStorage from '../utils/localStorage';

class StorageService {
  /**
   * Constructor
   * @param {string} storeName - Name of the store (used as localStorage prefix)
   */
  constructor(storeName) {
    this.storeName = storeName;
    this.key = `clinic_${storeName}`;
    this.initialize();
  }
  
  /**
   * Initialize storage
   * @private
   */
  initialize() {
    // Check if store exists, if not create it
    if (!LocalStorage.hasItem(this.key)) {
      LocalStorage.setItem(this.key, {});
    }
  }
  
  /**
   * Get all items as an object
   * @returns {Object} All items
   */
  getAll() {
    return LocalStorage.getItem(this.key, {});
  }
  
  /**
   * Get all items as an array
   * @returns {Array} All items as array
   */
  getAllAsArray() {
    const all = this.getAll();
    return Object.values(all);
  }
  
  /**
   * Get item by ID
   * @param {string} id - Item ID
   * @returns {Object|null} Item or null if not found
   */
  getById(id) {
    const all = this.getAll();
    return all[id] || null;
  }
  
  /**
   * Save item
   * @param {string} id - Item ID
   * @param {Object} item - Item data
   * @returns {boolean} Whether save was successful
   */
  save(id, item) {
    try {
      const all = this.getAll();
      all[id] = item;
      LocalStorage.setItem(this.key, all);
      return true;
    } catch (error) {
      console.error(`Error saving ${this.storeName} with ID ${id}:`, error);
      return false;
    }
  }
  
  /**
   * Delete item
   * @param {string} id - Item ID
   * @returns {boolean} Whether deletion was successful
   */
  delete(id) {
    try {
      const all = this.getAll();
      if (all[id] === undefined) {
        return false;
      }
      
      delete all[id];
      LocalStorage.setItem(this.key, all);
      return true;
    } catch (error) {
      console.error(`Error deleting ${this.storeName} with ID ${id}:`, error);
      return false;
    }
  }
  
  /**
   * Update item (partial update)
   * @param {string} id - Item ID
   * @param {Object} updates - Fields to update
   * @returns {boolean} Whether update was successful
   */
  update(id, updates) {
    try {
      const all = this.getAll();
      if (all[id] === undefined) {
        return false;
      }
      
      all[id] = {
        ...all[id],
        ...updates
      };
      LocalStorage.setItem(this.key, all);
      return true;
    } catch (error) {
      console.error(`Error updating ${this.storeName} with ID ${id}:`, error);
      return false;
    }
  }
  
  /**
   * Find items by predicate
   * @param {Function} predicate - Function to test each item
   * @returns {Array} Matching items
   */
  find(predicate) {
    try {
      const all = this.getAll();
      const result = [];
      
      Object.values(all).forEach(item => {
        if (predicate(item)) {
          result.push(item);
        }
      });
      
      return result;
    } catch (error) {
      console.error(`Error finding ${this.storeName}:`, error);
      return [];
    }
  }
  
  /**
   * Count items
   * @returns {number} Number of items
   */
  count() {
    const all = this.getAll();
    return Object.keys(all).length;
  }
  
  /**
   * Clear all items
   * @returns {boolean} Whether clear was successful
   */
  clear() {
    try {
      LocalStorage.setItem(this.key, {});
      return true;
    } catch (error) {
      console.error(`Error clearing ${this.storeName}:`, error);
      return false;
    }
  }
  
  /**
   * Import data from JSON
   * @param {string} jsonData - JSON string
   * @param {boolean} [merge=false] - Whether to merge with existing data
   * @returns {boolean} Whether import was successful
   */
  importData(jsonData, merge = false) {
    try {
      const importedData = typeof jsonData === 'string' ? JSON.parse(jsonData) : jsonData;
      
      if (!importedData || typeof importedData !== 'object') {
        console.error(`Invalid data format for ${this.storeName} import`);
        return false;
      }
      
      // Either merge or replace existing data
      let newData;
      if (merge) {
        newData = {
          ...this.getAll(),
          ...importedData
        };
      } else {
        newData = importedData;
      }
      
      LocalStorage.setItem(this.key, newData);
      return true;
    } catch (error) {
      console.error(`Error importing data for ${this.storeName}:`, error);
      return false;
    }
  }
  
  /**
   * Export data as JSON string
   * @returns {string} JSON string
   */
  exportData() {
    try {
      const data = this.getAll();
      return JSON.stringify(data, null, 2);
    } catch (error) {
      console.error(`Error exporting data for ${this.storeName}:`, error);
      return '{}';
    }
  }
  
  /**
   * Backup store data with timestamp
   * @param {string} [description=''] - Backup description
   * @returns {Object} Backup result
   */
  backup(description = '') {
    try {
      const data = this.getAll();
      const timestamp = new Date().toISOString();
      const backupKey = `backup_${this.storeName}_${timestamp.replace(/[:.]/g, '-')}`;
      
      const backupData = {
        timestamp,
        description,
        storeName: this.storeName,
        data
      };
      
      LocalStorage.setItem(backupKey, backupData);
      
      return {
        success: true,
        backupId: backupKey,
        timestamp,
        description,
        itemCount: Object.keys(data).length
      };
    } catch (error) {
      console.error(`Error creating backup for ${this.storeName}:`, error);
      return {
        success: false,
        error: error.message
      };
    }
  }
  
  /**
   * List available backups
   * @returns {Array} List of backups
   */
  listBackups() {
    try {
      const keys = LocalStorage.getKeys();
      const backupPrefix = `backup_${this.storeName}_`;
      
      const backups = keys
        .filter(key => key.startsWith(backupPrefix))
        .map(key => {
          const backup = LocalStorage.getItem(key);
          return {
            backupId: key,
            timestamp: backup.timestamp,
            description: backup.description,
            itemCount: Object.keys(backup.data || {}).length
          };
        })
        .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
      
      return backups;
    } catch (error) {
      console.error(`Error listing backups for ${this.storeName}:`, error);
      return [];
    }
  }
  
  /**
   * Restore from backup
   * @param {string} backupId - Backup ID
   * @returns {boolean} Whether restore was successful
   */
  restoreFromBackup(backupId) {
    try {
      const backup = LocalStorage.getItem(backupId);
      
      if (!backup || !backup.data || backup.storeName !== this.storeName) {
        console.error(`Invalid backup or mismatched store for ${this.storeName}`);
        return false;
      }
      
      LocalStorage.setItem(this.key, backup.data);
      return true;
    } catch (error) {
      console.error(`Error restoring backup for ${this.storeName}:`, error);
      return false;
    }
  }
}

export default StorageService;